import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { organizationService } from '../services';
import {
  Organization,
  OrganizationSettings,
  OrganizationMetrics,
  OrganizationMember,
  OrganizationApiKey,
  CrossOrgShareRequest,
  PatientTransfer,
  OrganizationTemplate,
} from '../types';

// Query Keys
export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (filters?: string) => [...organizationKeys.lists(), { filters }] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  settings: (id: string) => [...organizationKeys.all, 'settings', id] as const,
  metrics: (id: string, month?: string) => [...organizationKeys.all, 'metrics', id, month] as const,
  members: (id: string) => [...organizationKeys.all, 'members', id] as const,
  member: (orgId: string, userId: string) => [...organizationKeys.members(orgId), userId] as const,
  apiKeys: (id: string) => [...organizationKeys.all, 'api-keys', id] as const,
  apiKey: (orgId: string, keyId: string) => [...organizationKeys.apiKeys(orgId), keyId] as const,
  sharing: {
    outgoing: (id: string) => [...organizationKeys.all, 'sharing', 'outgoing', id] as const,
    incoming: (id: string) => [...organizationKeys.all, 'sharing', 'incoming', id] as const,
    active: (id: string) => [...organizationKeys.all, 'sharing', 'active', id] as const,
  },
  transfers: {
    outgoing: (id: string) => [...organizationKeys.all, 'transfers', 'outgoing', id] as const,
    incoming: (id: string) => [...organizationKeys.all, 'transfers', 'incoming', id] as const,
    pending: (id: string) => [...organizationKeys.all, 'transfers', 'pending', id] as const,
    byPatient: (patientId: string) => [...organizationKeys.all, 'transfers', 'patient', patientId] as const,
  },
  templates: {
    all: ['organization-templates'] as const,
    detail: (id: string) => ['organization-templates', id] as const,
  },
  usage: (id: string) => [...organizationKeys.all, 'usage', id] as const,
  hierarchy: (id: string) => [...organizationKeys.all, 'hierarchy', id] as const,
  children: (id: string) => [...organizationKeys.all, 'children', id] as const,
  parent: (id: string) => [...organizationKeys.all, 'parent', id] as const,
  myOrganizations: ['my-organizations'] as const,
};

// Hook to get user's organizations and primary organization
export const useMyOrganizations = () => {
  return useQuery({
    queryKey: organizationKeys.myOrganizations,
    queryFn: () => organizationService.getOrganizations(),
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to get user's primary organization ID
export const usePrimaryOrganizationId = (): string | undefined => {
  const { data: organizations } = useMyOrganizations();
  
  if (!organizations || organizations.length === 0) return undefined;
  
  // Find primary organization, or fall back to first one
  const primary = organizations.find((org: any) => org.isPrimary);
  return primary?.id || organizations[0]?.id;
};

// Organization Hooks
export const useOrganizations = (): UseQueryResult<Organization[], Error> => {
  return useQuery({
    queryKey: organizationKeys.lists(),
    queryFn: () => organizationService.getOrganizations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useOrganization = (id: string): UseQueryResult<Organization, Error> => {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => organizationService.getOrganization(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateOrganization = (): UseMutationResult<
  Organization,
  Error,
  Partial<Organization>
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Organization>) => organizationService.createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
};

export const useUpdateOrganization = (): UseMutationResult<
  Organization,
  Error,
  { id: string; data: Partial<Organization> }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Organization> }) =>
      organizationService.updateOrganization(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
};

export const useOrganizationUsage = (id: string) => {
  return useQuery({
    queryKey: organizationKeys.usage(id),
    queryFn: () => organizationService.getOrganizationUsage(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

export const useOrganizationHierarchy = (id: string) => {
  return useQuery({
    queryKey: organizationKeys.hierarchy(id),
    queryFn: () => organizationService.getOrganizationHierarchy(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// Organization Settings Hooks
export const useOrganizationSettings = (
  organizationId: string
): UseQueryResult<OrganizationSettings, Error> => {
  return useQuery({
    queryKey: organizationKeys.settings(organizationId),
    queryFn: () => organizationService.getOrganizationSettings(organizationId),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateOrganizationSettings = (): UseMutationResult<
  OrganizationSettings,
  Error,
  { organizationId: string; data: Partial<OrganizationSettings> }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, data }) =>
      organizationService.updateOrganizationSettings(organizationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.settings(variables.organizationId) });
    },
  });
};

// Organization Metrics Hooks
export const useOrganizationMetrics = (
  organizationId: string,
  month?: string
): UseQueryResult<OrganizationMetrics[], Error> => {
  return useQuery({
    queryKey: organizationKeys.metrics(organizationId, month),
    queryFn: () => organizationService.getOrganizationMetrics(organizationId, month),
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useOrganizationMetricsForMonth = (organizationId: string, month: string) => {
  return useQuery({
    queryKey: organizationKeys.metrics(organizationId, month),
    queryFn: () => organizationService.getOrganizationMetricsForMonth(organizationId, month),
    enabled: !!organizationId && !!month,
    staleTime: 2 * 60 * 1000,
  });
};

// Organization Members Hooks
export const useOrganizationMembers = (
  organizationId: string
): UseQueryResult<OrganizationMember[], Error> => {
  return useQuery({
    queryKey: organizationKeys.members(organizationId),
    queryFn: () => organizationService.getOrganizationMembers(organizationId),
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useOrganizationMember = (organizationId: string, userId: string) => {
  return useQuery({
    queryKey: organizationKeys.member(organizationId, userId),
    queryFn: () => organizationService.getOrganizationMember(organizationId, userId),
    enabled: !!organizationId && !!userId,
  });
};

export const useAddOrganizationMember = (): UseMutationResult<
  OrganizationMember,
  Error,
  { organizationId: string; email: string; firstName: string; lastName: string; role: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, email, firstName, lastName, role }) =>
      organizationService.addOrganizationMember(organizationId, { 
        userId: email, // email is used as userId
        email,
        firstName,
        lastName,
        role 
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.organizationId) });
    },
  });
};

export const useUpdateOrganizationMember = (): UseMutationResult<
  OrganizationMember,
  Error,
  { organizationId: string; userId: string; role: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, userId, role }) =>
      organizationService.updateOrganizationMember(organizationId, userId, { role }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.organizationId) });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.member(variables.organizationId, variables.userId)
      });
    },
  });
};

export const useRemoveOrganizationMember = (): UseMutationResult<
  void,
  Error,
  { organizationId: string; userId: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, userId }) =>
      organizationService.removeOrganizationMember(organizationId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.organizationId) });
    },
  });
};

export const useSetMemberPrimaryOrganization = (): UseMutationResult<
  void,
  Error,
  { organizationId: string; userId: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, userId }) =>
      organizationService.setMemberPrimaryOrganization(organizationId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(variables.organizationId) });
    },
  });
};

// Organization API Keys Hooks
export const useOrganizationApiKeys = (
  organizationId: string
): UseQueryResult<OrganizationApiKey[], Error> => {
  return useQuery({
    queryKey: organizationKeys.apiKeys(organizationId),
    queryFn: () => organizationService.getOrganizationApiKeys(organizationId),
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateOrganizationApiKey = (): UseMutationResult<
  { apiKey: OrganizationApiKey; keyValue: string },
  Error,
  { organizationId: string; keyName: string; expiryDate?: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, keyName, expiryDate }) =>
      organizationService.createOrganizationApiKey(organizationId, { keyName, expiryDate }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.apiKeys(variables.organizationId) });
    },
  });
};

export const useRevokeOrganizationApiKey = (): UseMutationResult<
  void,
  Error,
  { organizationId: string; keyId: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, keyId }) =>
      organizationService.revokeOrganizationApiKey(organizationId, keyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.apiKeys(variables.organizationId) });
    },
  });
};

// Cross-Organization Sharing Hooks
export const useOutgoingShareRequests = (
  organizationId: string
): UseQueryResult<CrossOrgShareRequest[], Error> => {
  return useQuery({
    queryKey: organizationKeys.sharing.outgoing(organizationId),
    queryFn: () => organizationService.getOutgoingShareRequests(organizationId),
    enabled: !!organizationId,
    staleTime: 1 * 60 * 1000,
  });
};

export const useIncomingShareRequests = (
  organizationId: string
): UseQueryResult<CrossOrgShareRequest[], Error> => {
  return useQuery({
    queryKey: organizationKeys.sharing.incoming(organizationId),
    queryFn: () => organizationService.getIncomingShareRequests(organizationId),
    enabled: !!organizationId,
    staleTime: 1 * 60 * 1000,
  });
};

export const useActiveShares = (
  organizationId: string
): UseQueryResult<CrossOrgShareRequest[], Error> => {
  return useQuery({
    queryKey: organizationKeys.sharing.active(organizationId),
    queryFn: () => organizationService.getActiveShares(organizationId),
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateShareRequest = (): UseMutationResult<
  CrossOrgShareRequest,
  Error,
  {
    organizationId: string;
    toOrganizationId: string;
    patientId: string;
    requestedDataTypes: string[];
    purpose: string;
    expiryDate?: string;
  }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, ...data }) =>
      organizationService.createShareRequest(organizationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.sharing.outgoing(variables.organizationId) });
    },
  });
};

export const useApproveShareRequest = (): UseMutationResult<
  void,
  Error,
  { organizationId: string; requestId: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, requestId }) =>
      organizationService.approveShareRequest(organizationId, requestId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.sharing.incoming(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.sharing.active(variables.organizationId) });
    },
  });
};

export const useDenyShareRequest = (): UseMutationResult<
  void,
  Error,
  { organizationId: string; requestId: string; reason: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, requestId, reason }) =>
      organizationService.denyShareRequest(organizationId, requestId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.sharing.incoming(variables.organizationId) });
    },
  });
};

export const useRevokeShare = (): UseMutationResult<
  void,
  Error,
  { organizationId: string; requestId: string }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, requestId }) =>
      organizationService.revokeShare(organizationId, requestId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.sharing.outgoing(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.sharing.active(variables.organizationId) });
    },
  });
};

// Patient Transfers Hooks
export const useOutgoingTransfers = (
  organizationId: string
): UseQueryResult<PatientTransfer[], Error> => {
  return useQuery({
    queryKey: organizationKeys.transfers.outgoing(organizationId),
    queryFn: () => organizationService.getOutgoingTransfers(organizationId),
    enabled: !!organizationId,
    staleTime: 1 * 60 * 1000,
  });
};

export const useIncomingTransfers = (
  organizationId: string
): UseQueryResult<PatientTransfer[], Error> => {
  return useQuery({
    queryKey: organizationKeys.transfers.incoming(organizationId),
    queryFn: () => organizationService.getIncomingTransfers(organizationId),
    enabled: !!organizationId,
    staleTime: 1 * 60 * 1000,
  });
};

export const usePendingTransfers = (
  organizationId: string
): UseQueryResult<PatientTransfer[], Error> => {
  return useQuery({
    queryKey: organizationKeys.transfers.pending(organizationId),
    queryFn: () => organizationService.getPendingTransfers(organizationId),
    enabled: !!organizationId,
    staleTime: 1 * 60 * 1000,
  });
};

export const usePatientTransfers = (patientId: string): UseQueryResult<PatientTransfer[], Error> => {
  return useQuery({
    queryKey: organizationKeys.transfers.byPatient(patientId),
    queryFn: () => organizationService.getPatientTransfers(patientId),
    enabled: !!patientId,
  });
};

export const useCreatePatientTransfer = (): UseMutationResult<
  PatientTransfer,
  Error,
  {
    patientId: string;
    fromOrganizationId: string;
    toOrganizationId: string;
    reason: string;
  }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => organizationService.createPatientTransfer(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.transfers.outgoing(variables.fromOrganizationId)
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.transfers.byPatient(variables.patientId)
      });
    },
  });
};

export const useApproveTransferByFromOrg = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transferId: string) => organizationService.approveTransferByFromOrg(transferId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

export const useApproveTransferByToOrg = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transferId: string) => organizationService.approveTransferByToOrg(transferId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

export const useCompleteTransfer = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transferId: string) => organizationService.completeTransfer(transferId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

export const useCancelTransfer = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transferId: string) => organizationService.cancelTransfer(transferId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

export const useDenyTransfer = (): UseMutationResult<void, Error, { transferId: string; reason: string }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, reason }) => organizationService.denyTransfer(transferId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
};

// Organization Templates Hooks
export const useOrganizationTemplates = (): UseQueryResult<OrganizationTemplate[], Error> => {
  return useQuery({
    queryKey: organizationKeys.templates.all,
    queryFn: () => organizationService.getAllTemplates(),
    staleTime: 10 * 60 * 1000, // 10 minutes - templates don't change often
  });
};

export const useOrganizationTemplate = (id: string): UseQueryResult<OrganizationTemplate, Error> => {
  return useQuery({
    queryKey: organizationKeys.templates.detail(id),
    queryFn: () => organizationService.getTemplate(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreateOrganizationFromTemplate = (): UseMutationResult<
  Organization,
  Error,
  {
    templateId: string;
    name: string;
    contactEmail: string;
    contactPhone: string;
    address: any;
  }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, ...data }) =>
      organizationService.createOrganizationFromTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
};
